import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import type { ClipPublic } from '@proref/types';
import { useRefereeClipsQuery } from '../../hooks/use-clips';
import { Skeleton } from '../shared/Skeleton';
import { Icon } from '../shared/Icons';

// "Recommended for you" — real published clips, picked in two tiers:
//
//   1. TARGETED: clips tagged to the referee's weakest rule (server-side filter). The design pulls
//      from several weak rules; we key off the single weakest that /me/stats already gives us, so
//      the recommendation is honest and needs no new endpoint.
//   2. FALLBACK: a random sample of the library, used ONLY when tier 1 yields nothing — either the
//      referee has no mastery yet (no weakest rule) or, far more commonly, no clip is tagged to
//      that rule. With a library covering a fraction of the rulebook the weakest rule usually has
//      no clip at all, and the section used to vanish for nearly every referee. Something to watch
//      beats an empty Home.
//
// Only a genuinely empty library hides the section now.

const PREVIEW_LIMIT = 3;
const DAY_MS = 86_400_000;

function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) return '—:—';
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// A SEEDED draw rather than Math.random: an unseeded one would deal a different three cards on
// every render (any parent state change re-runs this) and again after each background refetch, so
// cards would jump under the referee's cursor mid-click. Seeding on the day number keeps the sample
// fixed for the day but rotates it overnight, so the shelf still feels alive.
// Exported for testing — the seeding is the whole contract worth pinning.
export function pickFallbackClips(clips: ClipPublic[], limit: number, seed: number): ClipPublic[] {
  // Park-Miller LCG. seed % m can land on 0, the one state that makes the generator degenerate to a
  // constant (and so deal the same clip three times), hence the +1.
  let state = (Math.abs(Math.trunc(seed)) % 2147483646) + 1;
  const next = () => {
    state = (state * 48271) % 2147483647;
    return state / 2147483647;
  };

  // Draw without replacement by removing each pick from a copy of the pool, so the three cards are
  // always distinct even when the same index comes up twice.
  const pool = [...clips];
  const picked: ClipPublic[] = [];
  while (picked.length < limit && pool.length > 0) {
    const [taken] = pool.splice(Math.floor(next() * pool.length), 1);
    if (taken) picked.push(taken);
  }
  return picked;
}

interface RecommendedClipsProps {
  ruleArticleId?: string | null;
  ruleNumber?: string | null;
}

export function RecommendedClips({ ruleArticleId, ruleNumber }: RecommendedClipsProps) {
  const { t } = useTranslation();
  const hasRule = Boolean(ruleArticleId);

  const ruleQuery = useRefereeClipsQuery(ruleArticleId ?? undefined);
  // Same query key as HomePage's library count, so React Query serves this from cache — the
  // fallback costs no extra request. When there is no weakest rule this is literally the same
  // query as `ruleQuery` above; the hook call is deduped, not doubled.
  const libraryQuery = useRefereeClipsQuery(undefined);

  // An errored rule query counts as "nothing from the weakest rule" and falls through to the
  // sample, rather than blanking the section — the fallback is what the referee can still act on.
  const ruleClips = hasRule && !ruleQuery.isError ? (ruleQuery.data ?? []) : [];
  const ruleLoading = hasRule && ruleQuery.isLoading;
  const targeted = ruleClips.slice(0, PREVIEW_LIMIT);
  const isFallback = !ruleLoading && targeted.length === 0;

  const fallback = useMemo(
    () => (isFallback ? pickFallbackClips(libraryQuery.data ?? [], PREVIEW_LIMIT, Math.floor(Date.now() / DAY_MS)) : []),
    [isFallback, libraryQuery.data],
  );

  const loading = ruleLoading || (isFallback && libraryQuery.isLoading);
  const clips = isFallback ? fallback : targeted;

  // The library itself is empty (or unreadable) — there is nothing to recommend at any tier, so
  // hide the section entirely rather than show an empty shell.
  if (!loading && clips.length === 0) return null;

  return (
    <section className="rounded border border-line bg-surface p-5 shadow-1">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-semibold text-ink">{t('home.recommended.title')}</h2>
          <p className="mt-0.5 text-xs text-muted">
            {/* Deliberately never claims a weakest-rule pull while still loading: the fallback is
                the common case today, and a subtitle that swapped after the fetch would have
                asserted something untrue in the meantime. The two non-loading cases embed a styled
                "Rule N" span mid-sentence, so they go through <Trans> rather than a flat t() call —
                a plain string couldn't keep the emphasis styling, and splitting the sentence into
                prefix/suffix pieces around the span would bake in English word order, which
                Croatian/Romanian don't necessarily share.
                `components` (a NAMED tag, not positional numbering) is deliberate: Trans's
                positional `<0>`/`<1>` numbering counts EVERY child in the JSX — including a
                `{' '}` spacer, which is invisible in the JSX source but still consumes an index —
                so which number a given element lands on depends on incidental whitespace choices
                and silently drifts (found the hard way: two near-identical sentences needed two
                different numbers). A named tag has no such off-by-one to get wrong. */}
            {loading ? (
              t('home.recommended.finding')
            ) : isFallback ? (
              hasRule ? (
                <Trans
                  i18nKey="home.recommended.fallbackWithRule"
                  values={{ number: ruleNumber }}
                  components={{ ruleTag: <span className="font-medium text-ink-2" /> }}
                />
              ) : (
                t('home.recommended.fallbackNoRule')
              )
            ) : (
              <Trans
                i18nKey="home.recommended.pulledFromWeakest"
                values={{ number: ruleNumber }}
                components={{ ruleTag: <span className="font-medium text-ink-2" /> }}
              />
            )}
          </p>
        </div>
        <Link
          to="/clips"
          className="inline-flex flex-none items-center gap-1 text-xs font-medium text-accent hover:text-accent-2"
        >
          {t('home.recommended.seeAll')}
          <Icon.arrow className="h-3.5 w-3.5" />
        </Link>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {loading
          ? // Matches the loaded card (aspect-video poster + one title line) so the swap-in doesn't
            // shift the page under a referee who is already reaching for a card.
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="overflow-hidden rounded-sm border border-line-2">
                <Skeleton className="aspect-video w-full rounded-none" />
                <div className="px-3 py-2">
                  <Skeleton className="h-4 w-3/4" />
                </div>
              </div>
            ))
          : clips.map((clip) => <RecommendedCard key={clip.id} clip={clip} />)}
      </div>
    </section>
  );
}

function RecommendedCard({ clip }: { clip: ClipPublic }) {
  const { t } = useTranslation();
  const href = `/clips?clip=${clip.id}`;
  return (
    <Link
      to={href}
      aria-label={t('home.recommended.openClipAria', { title: clip.title })}
      className="group block overflow-hidden rounded-sm border border-line-2 bg-surface shadow-1 transition-colors hover:border-line-3"
    >
      {/* aspect-video, matching the 1280x720 thumbnail MediaConvert writes — so the whole frame is
          shown rather than centre-cropped. A fixed height here (the strip used h-24) fights the
          grid's fluid column width and slices the top and bottom off every poster; the referee
          library and Bookmarked use aspect-video for the same reason. */}
      <div className="relative aspect-video w-full overflow-hidden bg-bg-2">
        {clip.poster_url ? (
          <img src={clip.poster_url} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="grid h-full w-full place-items-center text-[11px] text-muted">
            {t('home.recommended.noPoster')}
          </div>
        )}
        {/* Rule tag, mirroring the design's corner label. */}
        {clip.rule_article && (
          <span className="absolute left-2 top-2 rounded-sm bg-ink/85 px-1.5 py-0.5 text-[10px] font-semibold text-bg">
            {t('common.ruleLabel', { number: clip.rule_article.article_number })}
          </span>
        )}
        <span className="absolute inset-0 grid place-items-center">
          <span className="grid h-8 w-8 place-items-center rounded-full bg-ink/50 text-white shadow-1 backdrop-blur transition-colors group-hover:bg-ink/65">
            <Icon.play className="h-3.5 w-3.5" />
          </span>
        </span>
        <span className="absolute bottom-1.5 right-1.5 rounded-sm bg-ink/85 px-1.5 py-0.5 text-[10px] font-medium text-bg">
          {formatDuration(clip.duration_sec)}
        </span>
      </div>
      <div className="px-3 py-2">
        <div className="line-clamp-1 text-[13px] font-medium text-ink">{clip.title}</div>
      </div>
    </Link>
  );
}
